import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Users, Server, Globe, TrendingUp } from 'lucide-react';

// Define TypeScript interfaces
interface Metrics {
  totalMeters: number;
  totalCustomers: number;
  regionsCovered: number;
  waterSavedLiters: number;
}

interface GrowthData {
  name: string;
  value: number;
}

type RangeType = 'monthly' | 'quarterly' | 'yearly';

// Dummy API fetch functions (replace with your real API calls)
const fetchMetrics = async (): Promise<Metrics> => {
  // Simulate API delay
  return new Promise<Metrics>((resolve) => {
    setTimeout(() => {
      resolve({
        totalMeters: 1250,
        totalCustomers: 980,
        regionsCovered: 12,
        waterSavedLiters: 4567890,
      });
    }, 500);
  });
};

const fetchGrowthData = async (range: RangeType): Promise<GrowthData[]> => {
  // Simulate fetching growth data based on range: 'monthly', 'quarterly', 'yearly'
  return new Promise<GrowthData[]>((resolve) => {
    setTimeout(() => {
      if (range === 'monthly') {
        resolve([
          { name: 'Jan', value: 80 },
          { name: 'Feb', value: 100 },
          { name: 'Mar', value: 120 },
          { name: 'Apr', value: 150 },
          { name: 'May', value: 170 },
          { name: 'Jun', value: 200 },
          { name: 'Jul', value: 230 },
          { name: 'Aug', value: 260 },
          { name: 'Sep', value: 300 },
          { name: 'Oct', value: 330 },
          { name: 'Nov', value: 360 },
          { name: 'Dec', value: 400 },
        ]);
      } else if (range === 'quarterly') {
        resolve([
          { name: 'Q1', value: 300 },
          { name: 'Q2', value: 450 },
          { name: 'Q3', value: 600 },
          { name: 'Q4', value: 800 },
        ]);
      } else if (range === 'yearly') {
        resolve([
          { name: '2021', value: 1200 },
          { name: '2022', value: 1800 },
          { name: '2023', value: 2400 },
          { name: '2024', value: 3000 },
          { name: '2025', value: 3600 },
        ]);
      } else {
        resolve([]);
      }
    }, 500);
  });
};

const DashboardStats: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics>({
    totalMeters: 0,
    totalCustomers: 0,
    regionsCovered: 0,
    waterSavedLiters: 0,
  });
  const [growthData, setGrowthData] = useState<GrowthData[]>([]);
  const [range, setRange] = useState<RangeType>('monthly');
  const [loadingMetrics, setLoadingMetrics] = useState<boolean>(true);
  const [loadingGrowth, setLoadingGrowth] = useState<boolean>(true);

  useEffect(() => {
    const getMetrics = async () => {
      setLoadingMetrics(true);
      try {
        const data: Metrics = await fetchMetrics();
        setMetrics(data);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoadingMetrics(false);
      }
    };
    getMetrics();
  }, []);

  useEffect(() => {
    const getGrowth = async () => {
      setLoadingGrowth(true);
      try {
        const data: GrowthData[] = await fetchGrowthData(range);
        setGrowthData(data);
      } catch (error) {
        console.error('Error fetching growth data:', error);
      } finally {
        setLoadingGrowth(false);
      }
    };
    getGrowth();
  }, [range]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.2 } }),
  };

  const metricsList = [
    {
      label: 'Meters Installed',
      value: 240,
      icon: <Server className="w-6 h-6 text-blue-800" />, // example icon
      suffix: '+',
    },
    {
      label: 'Customers',
      value: 4315,
      icon: <Users className="w-6 h-6 text-blue-800" />, // example icon
      suffix: '+',
    },
    {
      label: 'Regions Covered',
      value: 17,
      icon: <Globe className="w-6 h-6 text-blue-800" />,
      suffix: '',
    },
    {
      label: 'Water Saved (L)',
      value: metrics.waterSavedLiters,
      icon: <TrendingUp className="w-6 h-6 text-blue-800" />,
      suffix: '',
    },
  ];

  return (
    <section className="container mx-auto px-4 py-12">
      <h2 className="font-oswald mb-4 text-center text-2xl font-bold uppercase text-blue-800 md:mb-8 md:text-4xl lg:text-6xl">
         Progresion
      </h2>
      <p className="font-poppins mx-auto mb-8 max-w-2xl text-center text-gray-600 md:mb-12">
                        When developing our products, we primarily focus on
                        three main pillars - reliability, security, and ease of
                        use. We believe these elements are essential for the
                        success of any IoT project.
                    </p>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {metricsList.map((item, idx) => (
          <motion.div
            key={idx}
            custom={idx}
            initial="hidden"
            animate="visible"
            variants={cardVariants}
            className="flex flex-col items-center justify-center rounded-2xl bg-white p-6 shadow-lg hover:shadow-2xl transition-shadow"
          >
            <div className="mb-4 flex items-center justify-center rounded-full bg-blue-100 p-3">
              {item.icon}
            </div>
            <div className="text-2xl font-semibold text-gray-800">
              {loadingMetrics ? (
                <span>...</span>
              ) : (
                <CountUp end={item.value} duration={2} separator="," />
              )}
              {item.suffix}
            </div>
            <div className="mt-2 text-sm text-gray-500 text-center">
              {item.label}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Growth Chart
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
          <h3 className="font-oswald text-xl font-semibold text-gray-800">
            Growth Overview
          </h3>
          <div className="mt-4 md:mt-0">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as RangeType)}
              className="rounded-lg border border-gray-300 bg-white py-1 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
        </div>
        {loadingGrowth ? (
          <div className="flex justify-center py-12 text-gray-500">Loading chart...</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={growthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#1D4ED8"
                strokeWidth={3}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div> */}
    </section>
  );
};

export default DashboardStats;
